import { useCallback, useMemo } from 'react';

import {
  CreateUserRequest,
  UpdateUserMfaRequest,
  UpdateUserRequest,
  useCreateUser,
  useDeleteUser,
  useListUsers,
  useUpdateUser,
  useUpdateUserMfa
} from '@/api/generated';

const useUsers = (includeStatus?: boolean) => {
  const {
    data: usersData,
    refetch: refetchUsers,
    isLoading: usersLoading
  } = useListUsers(
    { includeStatus: includeStatus },
    {
      query: {
        queryKey: ['listUsers', includeStatus]
      }
    }
  );

  const usersList = useMemo(() => usersData?.items || [], [usersData?.items]);

  const { mutateAsync: mutateUpdateUser } = useUpdateUser();
  const { mutateAsync: mutateUpdateUserMfa } = useUpdateUserMfa();
  const { mutateAsync: mutateDeleteUser } = useDeleteUser();
  const { mutateAsync: mutateCreateUser } = useCreateUser();

  const createUser = useCallback(
    async (data: CreateUserRequest, sendInviteEmail = false) => {
      const response = await mutateCreateUser({
        data: { ...data, sendInviteEmail: sendInviteEmail }
      });
      return response?.items?.[0];
    },
    [mutateCreateUser]
  );

  const updateUser = useCallback(
    async (id: string, request: UpdateUserRequest) => {
      await mutateUpdateUser({ id, data: request });
    },
    [mutateUpdateUser]
  );

  const updateUserMfa = useCallback(
    async (id: string, request: UpdateUserMfaRequest) => {
      await mutateUpdateUserMfa({ id, data: request });
    },
    [mutateUpdateUserMfa]
  );

  const deleteUser = useCallback(
    async (id: string) => {
      await mutateDeleteUser({ id });
    },
    [mutateDeleteUser]
  );

  return useMemo(
    () => ({
      usersList,
      usersLoading,
      createUser,
      refetchUsers,
      updateUser,
      updateUserMfa,
      deleteUser
    }),
    [usersList, usersLoading, refetchUsers, updateUser, updateUserMfa]
  );
};

export default useUsers;
